const STORE_NAME = 'tenantSettings'
const ORDER_TYPE_MV_PROPOSAL_OPTION_SETTING_KEY = 'project_order_type_mv_proposal_option_id'
const ORDER_TYPE_EV_PROPOSAL_OPTION_SETTING_KEY = 'project_order_type_ev_proposal_option_id'
// The tenant's damage_status rows for each step of the report workflow
const DAMAGE_STATUS_IN_PROGRESS_SETTING_KEY = 'damage_status_in_progress_id'
const DAMAGE_STATUS_IN_REVIEW_SETTING_KEY = 'damage_status_in_review_id'
const DAMAGE_STATUS_TO_SIGN_SETTING_KEY = 'damage_status_to_sign_id'
const DAMAGE_STATUS_DONE_SETTING_KEY = 'damage_status_done_id'
// The contact groups a reviewer hands a released report to for signing
const REVIEWER_READABLE_CONTACT_GROUPS_SETTING_KEY = 'reviewer_readable_contact_groups'

let settingsRequest: Promise<void> | null = null

export const useTenantSettingsStore = defineStore(STORE_NAME, {
  state: () => ({
    settings: {} as Record<string, string | null>,
    isFetched: false
  }),
  getters: {
    orderTypeMvProposalOptionId: (state) =>
      Number(state.settings[ORDER_TYPE_MV_PROPOSAL_OPTION_SETTING_KEY]) || null,
    orderTypeEvProposalOptionId: (state) =>
      Number(state.settings[ORDER_TYPE_EV_PROPOSAL_OPTION_SETTING_KEY]) || null,
    damageStatusInProgressId: (state) =>
      Number(state.settings[DAMAGE_STATUS_IN_PROGRESS_SETTING_KEY]) || null,
    damageStatusInReviewId: (state) =>
      Number(state.settings[DAMAGE_STATUS_IN_REVIEW_SETTING_KEY]) || null,
    damageStatusToSignId: (state) =>
      Number(state.settings[DAMAGE_STATUS_TO_SIGN_SETTING_KEY]) || null,
    damageStatusDoneId: (state) => Number(state.settings[DAMAGE_STATUS_DONE_SETTING_KEY]) || null,
    reviewerReadableContactGroupIds: (state): number[] =>
      String(state.settings[REVIEWER_READABLE_CONTACT_GROUPS_SETTING_KEY] || '')
        .split(',')
        .map(Number)
        .filter(Boolean),
    // A signed report carries the done status
    isDamageDone(): (damage: Damage | null) => boolean {
      return (damage) =>
        !!damage?.damage_status_id && damage.damage_status_id === this.damageStatusDoneId
    },
    // A report released for signing waits for its signature
    isDamageToSign(): (damage: Damage | null) => boolean {
      return (damage) =>
        !!damage?.damage_status_id && damage.damage_status_id === this.damageStatusToSignId
    },
    isDamageInReview(): (damage: Damage | null) => boolean {
      return (damage) =>
        !!damage?.damage_status_id && damage.damage_status_id === this.damageStatusInReviewId
    },
    // A report without a status is opened for the first time, so it is still in progress
    isDamageInProgress(): (damage: Damage | null) => boolean {
      return (damage) =>
        !damage?.damage_status_id || damage.damage_status_id === this.damageStatusInProgressId
    },
    // A reviewed report waits for its signature or is already signed
    reviewedDamageStatusIds(): number[] {
      return [this.damageStatusToSignId, this.damageStatusDoneId].filter(
        (statusId): statusId is number => !!statusId
      )
    }
  },
  actions: {
    // Loaded once per session; parallel callers share the same request.
    async fetchTenantSettings() {
      if (this.isFetched) return

      settingsRequest ||= useApi('css/tenant/settings', 'GET').then((response) => {
        this.settings = response?.data ?? {}
        this.isFetched = true
      })

      await settingsRequest
    }
  }
})
